'use client'

import { ImagePlaceholder } from '@/components/ui/image-placeholder'
import { useCurrency } from '@/hooks/use-currency'
import { useTapLock } from '@/hooks/use-tap-lock'
import { useAppDispatch } from '@/lib/store'
import { addToCart, openCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { ProductItem } from '@/lib/types/operations/product'
import { resolveUnitOfMeasure } from '@/lib/types/core/unit-of-measure'
import { resolvePrice, type PriceTier } from '@/lib/utils/price-resolution'
import { showToast, ToastSuppress } from '@/lib/utils/toast'
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'

interface ProductInventoryStatus {
  hasVariants: boolean
  inStock: boolean
  availableQuantity: number
  variantInventory: Array<{
    variantId: string
    inventory: number
    inStock: boolean
  }>
}

/** The bar sits on top of the phone bottom nav, and on the viewport when
 *  there is none. max(0px, …) guards the frame where the nav is gone but its
 *  scroll-hide offset has not been cleared yet. */
const BAR_BOTTOM = 'max(0px, calc(var(--app-bottom-nav-h, 0px) - var(--app-bottom-nav-offset, 0px)))'

interface ProductStickyCTAProps {
  product: ProductItem
  productStats: ProductInventoryStatus
  priceTier?: PriceTier
}

export function ProductStickyCTA({ product, productStats, priceTier = 'RETAIL' }: ProductStickyCTAProps) {
  const dispatch = useAppDispatch()
  const { format: formatCurrency } = useCurrency()
  const [visible, setVisible] = useState(false)
  // Synchronous lock: a rapid double-tap must not double the cart quantity
  const acquireTapLock = useTapLock()

  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0

  const lowestVariantPrice = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return product.price
    const prices = product.variants
      .map(v => resolvePrice(
        {
          retailPrice: v.retailPrice ?? v.pricePerUnit ?? null,
          wholesalePrice: v.wholesalePrice ?? null,
          rollPrice: v.rollPrice ?? null,
          rollLengthUnits: v.rollLengthUnits ?? null
        },
        priceTier
      ))
      .filter((p): p is number => p != null && p > 0)
    return prices.length > 0 ? Math.min(...prices) : product.price
  }, [product.variants, product.price, priceTier])

  const displayPrice = hasVariants ? lowestVariantPrice : product.price

  // Show the bar once the page has scrolled past the hero.
  //
  // Where it sits is not this component's business: BottomNav publishes the
  // bar's live height and scroll-hide offset on <html>, so BAR_BOTTOM reads
  // them and the CTA rides down with the nav and sits flush on the viewport
  // when there is no nav. Duplicating the 72px here meant an admin session
  // (no nav) left the CTA floating over a 72px gap, and a second scroll
  // accumulator could drift out of step with the nav's own.
  useEffect(() => {
    const threshold = 300
    const onScroll = () => setVisible(window.scrollY > threshold)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleAddToCart = useCallback(() => {
    if (!product.id || hasVariants) return
    // Second tap inside the lock window is a no-op, not a quantity increment
    if (!acquireTapLock()) return
    const productUnitOfMeasure = resolveUnitOfMeasure(product)
    dispatch(addToCart({
      productId: product.id,
      quantity: 1,
      product,
      unitOfMeasure: productUnitOfMeasure
    }))
    dispatch(openCartDrawer())
    showToast({
      title: 'Added to cart',
      color: 'success',
      timeout: 3000,
      skipIf: ToastSuppress.onMobile
    })
  }, [dispatch, product, hasVariants, acquireTapLock])

  const handlePress = useCallback(() => {
    if (hasVariants) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      handleAddToCart()
    }
  }, [hasVariants, handleAddToCart])

  return (
    <div
      className={`product-sticky-bar fixed left-0 right-0 z-[1001] md:hidden border-t border-divider bg-background/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.12)] transition-[transform,translate] duration-300 ease-out ${
        // Hidden travels its own height PLUS the nav gap it stands on;
        // translate-y-full alone parked the opaque bar directly over the
        // bottom nav and hid the tabs at the top of every product page.
        visible ? 'translate-y-0' : 'translate-y-[calc(100%_+_var(--bar-bottom))]'
      }`}
      style={{ '--bar-bottom': BAR_BOTTOM, bottom: 'var(--bar-bottom)' } as CSSProperties}
    >
      <div className='px-4 py-3 flex items-center gap-3'>
        {/* Product Thumbnail */}
        <div className='shrink-0 w-11 h-11 rounded-full overflow-hidden bg-surface border border-divider'>
          {product.media?.[0]?.url ? (
            <img src={product.media[0].url} alt={product.name} className='w-full h-full object-cover' />
          ) : (
            <ImagePlaceholder
              alt={`Image needed: product photo of ${product.name}`}
              className='w-full h-full'
              rounded='rounded-full'
              iconSize='sm'
            />
          )}
        </div>

        {/* Name + Price */}
        <div className='flex-1 min-w-0'>
          <p className='text-sm text-foreground font-semibold truncate leading-tight'>{product.name}</p>
          <p className='text-sm text-muted'>
            {hasVariants ? `From ${formatCurrency(displayPrice)}` : formatCurrency(displayPrice)}
          </p>
        </div>

        {/* CTA */}
        <button
          type='button'
          disabled={!productStats.inStock}
          onClick={handlePress}
          data-testid='add-to-cart-cta'
          className='shrink-0 text-sm font-semibold text-foreground hover:opacity-70 disabled:text-muted disabled:cursor-not-allowed transition-colors px-2 py-1'
        >
          {hasVariants ? 'Select Options' : 'Add to Cart'}
        </button>
      </div>
    </div>
  )
}
